// src/contexts/ProjektContext.js
// Globaler Context für Projekt-Management

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { 
  getAllProjekte, 
  getProjekteForUser, 
  getMitarbeiterProjekte,
  canSwitchProjects,
  ROLES 
} from '../services/firebaseProjektService';
import { useUser } from '../UserContext';

// Initialer State
const initialState = {
  projekte: [],
  aktuellesProjekt: null,
  userProjekte: [],
  loading: false,
  error: null,
  canSwitch: false
};

// Action Types
const PROJEKT_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_PROJEKTE: 'SET_PROJEKTE',
  SET_AKTUELLES_PROJEKT: 'SET_AKTUELLES_PROJEKT',
  SET_USER_PROJEKTE: 'SET_USER_PROJEKTE',
  SET_CAN_SWITCH: 'SET_CAN_SWITCH',
  ADD_PROJEKT: 'ADD_PROJEKT',
  UPDATE_PROJEKT: 'UPDATE_PROJEKT',
  DELETE_PROJEKT: 'DELETE_PROJEKT'
};

// Reducer
function projektReducer(state, action) {
  switch (action.type) {
    case PROJEKT_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case PROJEKT_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case PROJEKT_ACTIONS.SET_PROJEKTE:
      return { ...state, projekte: action.payload, loading: false };
    
    case PROJEKT_ACTIONS.SET_AKTUELLES_PROJEKT:
      return { ...state, aktuellesProjekt: action.payload };
    
    case PROJEKT_ACTIONS.SET_USER_PROJEKTE:
      return { ...state, userProjekte: action.payload };
    
    case PROJEKT_ACTIONS.SET_CAN_SWITCH:
      return { ...state, canSwitch: action.payload };
    
    case PROJEKT_ACTIONS.ADD_PROJEKT:
      return { 
        ...state, 
        projekte: [...state.projekte, action.payload],
        userProjekte: [...state.userProjekte, action.payload]
      };
    
    case PROJEKT_ACTIONS.UPDATE_PROJEKT:
      return {
        ...state,
        projekte: state.projekte.map(p => 
          p.id === action.payload.id ? action.payload : p
        ),
        userProjekte: state.userProjekte.map(p => 
          p.id === action.payload.id ? action.payload : p
        ),
        aktuellesProjekt: state.aktuellesProjekt?.id === action.payload.id 
          ? action.payload 
          : state.aktuellesProjekt
      };
    
    case PROJEKT_ACTIONS.DELETE_PROJEKT:
      return {
        ...state,
        projekte: state.projekte.filter(p => p.id !== action.payload),
        userProjekte: state.userProjekte.filter(p => p.id !== action.payload),
        aktuellesProjekt: state.aktuellesProjekt?.id === action.payload 
          ? null 
          : state.aktuellesProjekt
      };
    
    default:
      return state;
  }
}

// Context erstellen
const ProjektContext = createContext();

// Provider Component
export function ProjektProvider({ children }) {
  const [state, dispatch] = useReducer(projektReducer, initialState);
  const { user } = useUser();

  // Projekte laden basierend auf User-Rolle
  const loadProjekte = async () => {
    if (!user) {
      console.log('ProjektContext: Kein User vorhanden');
      return;
    }

    try {
      console.log('ProjektContext: Lade Projekte für User:', user.role, user.uid);
      dispatch({ type: PROJEKT_ACTIONS.SET_LOADING, payload: true });
      
      // Prüfen, ob User zwischen Projekten wechseln darf
      const canSwitch = canSwitchProjects(user.role);
      console.log('ProjektContext: canSwitch =', canSwitch);
      dispatch({ type: PROJEKT_ACTIONS.SET_CAN_SWITCH, payload: canSwitch });
      
      let projekte;
      if (canSwitch) {
        // Alle Projekte laden (für höhere Rollen)
        console.log('ProjektContext: Lade alle Projekte');
        projekte = await getAllProjekte();
        console.log('ProjektContext: Alle Projekte geladen:', projekte.length);
        dispatch({ type: PROJEKT_ACTIONS.SET_PROJEKTE, payload: projekte });
      } else {
        // Nur zugewiesene Projekte laden
        console.log('ProjektContext: Lade zugewiesene Projekte');
        const userProjekte = await getMitarbeiterProjekte(user.uid);
        console.log('ProjektContext: User-Projekte gefunden:', userProjekte.length);
        const projektListe = userProjekte.map(zuordnung => zuordnung.projekt);
        dispatch({ type: PROJEKT_ACTIONS.SET_USER_PROJEKTE, payload: projektListe });
        dispatch({ type: PROJEKT_ACTIONS.SET_PROJEKTE, payload: projektListe });
      }
      
    } catch (error) {
      console.error('ProjektContext: Fehler beim Laden der Projekte:', error);
      dispatch({ type: PROJEKT_ACTIONS.SET_ERROR, payload: error.message });
      
      // Fallback: Versuche trotzdem alle Projekte zu laden
      try {
        console.log('ProjektContext: Fallback - Lade alle Projekte');
        const fallbackProjekte = await getAllProjekte();
        console.log('ProjektContext: Fallback-Projekte geladen:', fallbackProjekte.length);
        dispatch({ type: PROJEKT_ACTIONS.SET_PROJEKTE, payload: fallbackProjekte });
        dispatch({ type: PROJEKT_ACTIONS.SET_CAN_SWITCH, payload: true });
      } catch (fallbackError) {
        console.error('ProjektContext: Auch Fallback fehlgeschlagen:', fallbackError);
      }
    }
  };

  // Aktuelles Projekt setzen
  const setAktuellesProjekt = (projekt) => {
    if (projekt) {
      // In localStorage speichern
      localStorage.setItem('lastProjektId', projekt.id);
      localStorage.setItem('lastProjektName', projekt.name);
    } else {
      // Aus localStorage entfernen
      localStorage.removeItem('lastProjektId');
      localStorage.removeItem('lastProjektName');
    }
    
    dispatch({ type: PROJEKT_ACTIONS.SET_AKTUELLES_PROJEKT, payload: projekt });
  };

  // Projekt aus localStorage wiederherstellen
  const restoreAktuellesProjekt = () => {
    const projektId = localStorage.getItem('lastProjektId');
    if (projektId && state.projekte.length > 0) {
      const projekt = state.projekte.find(p => p.id === projektId);
      if (projekt) {
        dispatch({ type: PROJEKT_ACTIONS.SET_AKTUELLES_PROJEKT, payload: projekt });
        return;
      }
    }
    
    // Fallback: Erstes verfügbares Projekt
    if (state.projekte.length > 0) {
      setAktuellesProjekt(state.projekte[0]);
    }
  };

  // Projekt hinzufügen
  const addProjekt = (projekt) => {
    dispatch({ type: PROJEKT_ACTIONS.ADD_PROJEKT, payload: projekt });
  };

  // Projekt aktualisieren
  const updateProjekt = (projekt) => {
    dispatch({ type: PROJEKT_ACTIONS.UPDATE_PROJEKT, payload: projekt });
  };

  // Projekt löschen
  const deleteProjekt = (projektId) => {
    dispatch({ type: PROJEKT_ACTIONS.DELETE_PROJEKT, payload: projektId });
  };

  // Projekte neu laden
  const refreshProjekte = () => {
    loadProjekte();
  };

  // Effect: Projekte laden wenn User sich ändert
  useEffect(() => {
    if (user) {
      loadProjekte();
    }
  }, [user]);

  // Effect: Aktuelles Projekt wiederherstellen wenn Projekte geladen sind
  useEffect(() => {
    if (state.projekte.length > 0 && !state.aktuellesProjekt) {
      restoreAktuellesProjekt();
    }
  }, [state.projekte]);

  const value = {
    // State
    projekte: state.projekte,
    aktuellesProjekt: state.aktuellesProjekt,
    userProjekte: state.userProjekte,
    loading: state.loading,
    error: state.error,
    canSwitch: state.canSwitch,
    
    // Actions
    setAktuellesProjekt,
    addProjekt,
    updateProjekt,
    deleteProjekt,
    refreshProjekte,
    loadProjekte
  };

  return (
    <ProjektContext.Provider value={value}>
      {children}
    </ProjektContext.Provider>
  );
}

// Custom Hook
export function useProjekt() {
  const context = useContext(ProjektContext);
  if (!context) {
    throw new Error('useProjekt muss innerhalb eines ProjektProviders verwendet werden');
  }
  return context;
}

// Hilfsfunktionen
export const getProjektById = (projekte, projektId) => {
  return projekte.find(p => p.id === projektId);
};

export const getProjektName = (projekte, projektId) => {
  const projekt = getProjektById(projekte, projektId);
  return projekt ? projekt.name : 'Unbekanntes Projekt';
};
